<script setup>
import { computed, nextTick, reactive, ref } from 'vue';
import { Virtualizer } from 'virtua/vue';
import Message from './Message.vue';
import ConversationThreadSeparator from 'dashboard/components/widgets/conversation/ConversationThreadSeparator.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';

const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
  threadSeparators: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);

const VIRTUAL_SCROLL_THRESHOLD = 80;
const virtualizerRef = ref(null);

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: [
      'content_attributes.translations',
      'content_attributes.whatsapp_flow_response.response_json',
    ],
  });
});

const currentChat = useMapGetter('getSelectedChat');
const activeConversationId = useMapGetter('getActiveConversationId');

const fetchedReplyMessages = reactive(new Map());

const separatorByMessageId = computed(() => {
  const map = new Map();
  props.threadSeparators.forEach(separator => {
    const messageId = separator.beforeMessageId || separator.before_message_id;
    if (messageId) {
      map.set(messageId, useCamelCase(separator));
    }
  });
  return map;
});

const timelineItems = computed(() => {
  const items = [];
  allMessages.value.forEach(message => {
    const separator = separatorByMessageId.value.get(message.id);
    if (separator) {
      items.push({
        type: 'separator',
        key: `separator-${message.id}`,
        separator,
      });
    }
    items.push({ type: 'message', key: `message-${message.id}`, message });
  });
  return items;
});

const useVirtualScroll = computed(
  () => timelineItems.value.length >= VIRTUAL_SCROLL_THRESHOLD
);

const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

const fetchReplyMessage = async (messageId, conversationId) => {
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  if (!replyMessage && activeConversationId.value) {
    fetchReplyMessage(inReplyToMessageId, activeConversationId.value);
    return null;
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};

const messageIndexById = computed(() => {
  const map = new Map();
  allMessages.value.forEach((message, index) => {
    map.set(message.id, index);
  });
  return map;
});

const scrollToMessage = async messageId => {
  const targetId = Number(messageId);
  if (!targetId) return false;

  const timelineIndex = timelineItems.value.findIndex(
    item => item.type === 'message' && Number(item.message.id) === targetId
  );
  if (timelineIndex < 0) return false;

  if (useVirtualScroll.value && virtualizerRef.value?.scrollToIndex) {
    virtualizerRef.value.scrollToIndex(timelineIndex, { align: 'start' });
    await nextTick();
  }

  const messageElement = document.getElementById(`message${targetId}`);
  if (!messageElement) return false;

  messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  return true;
};

defineExpose({ scrollToMessage });
</script>

<template>
  <ul class="px-4 bg-n-surface-1">
    <slot name="beforeAll" />
    <Virtualizer
      v-if="useVirtualScroll"
      ref="virtualizerRef"
      v-slot="{ item }"
      :data="timelineItems"
      :item-size="96"
    >
      <template v-if="item.type === 'separator'">
        <ConversationThreadSeparator :separator="item.separator" />
      </template>
      <template v-else>
        <slot
          v-if="firstUnreadId && item.message.id === firstUnreadId"
          name="unreadBadge"
        />
        <Message
          v-bind="item.message"
          :is-email-inbox="isAnEmailChannel"
          :in-reply-to="getInReplyToMessage(item.message)"
          :group-with-next="
            shouldGroupWithNext(
              messageIndexById.get(item.message.id),
              allMessages
            )
          "
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :current-user-id="currentUserId"
          data-clarity-mask="True"
          @retry="emit('retry', item.message)"
        />
      </template>
    </Virtualizer>
    <template v-else>
      <template v-for="item in timelineItems" :key="item.key">
        <ConversationThreadSeparator
          v-if="item.type === 'separator'"
          :separator="item.separator"
        />
        <template v-else>
          <slot
            v-if="firstUnreadId && item.message.id === firstUnreadId"
            name="unreadBadge"
          />
          <Message
            v-bind="item.message"
            :is-email-inbox="isAnEmailChannel"
            :in-reply-to="getInReplyToMessage(item.message)"
            :group-with-next="
              shouldGroupWithNext(
                messageIndexById.get(item.message.id),
                allMessages
              )
            "
            :inbox-supports-reply-to="inboxSupportsReplyTo"
            :current-user-id="currentUserId"
            data-clarity-mask="True"
            @retry="emit('retry', item.message)"
          />
        </template>
      </template>
    </template>
    <slot name="after" />
  </ul>
</template>
